import { FC } from 'react'
import classNames from 'classnames'
import { Card } from '@cmsgov/ds-medicare-gov'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useTranslation } from 'react-i18next'
import { MyInternalLink, MyLink } from 'components/MyLink'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { CLAIMS_URLS_ENGLISH, CLAIMS_URLS_SPANISH } from '../urls'

interface ContentCardProps {
  heading: string
  description: string
  buttonLink: string
  buttonText: string
}

const ContentCard: FC<ContentCardProps> = ({
  heading,
  description,
  buttonLink,
  buttonText
}) => {
  // Make the card the full height of its container, with the button aligned to the bottom.
  const cardClasses = [
    'ds-u-align-items--start',
    'ds-u-display--flex',
    'ds-u-flex-direction--column',
    'ds-u-justify-content--between',
    'ds-u-padding--3',
    'my-u-height-full'
  ]
  const buttonClasses = 'ds-c-button'
  const isExternalLink = /^https?:\/\//.test(buttonLink)

  return (
    <Card className={classNames(cardClasses)}>
      <div>
        <h3 className="ds-text-heading--xl">{heading}</h3>
        <p>{description}</p>
      </div>
      {isExternalLink
        ? (
        <MyLink href={buttonLink} className={buttonClasses}>
          {buttonText}
        </MyLink>
          )
        : (
        <MyInternalLink to={buttonLink} className={buttonClasses}>
          {buttonText}
        </MyInternalLink>
          )}
    </Card>
  )
}

export interface AccidentAndInjuryClaimsProps {
  className?: string
}

export const AccidentAndInjuryClaims: FC<AccidentAndInjuryClaimsProps> = ({
  className
}) => {
  const { t } = useTranslation('claimsSearch')

  const flags = useFlags()

  const mspButtonLegacyLink = useMedicareUrl(
    CLAIMS_URLS_ENGLISH.mspDisclaimer,
    CLAIMS_URLS_SPANISH.mspDisclaimer
  )
  const msaButtonLegacyLink = useMedicareUrl(
    CLAIMS_URLS_ENGLISH.msaDisclaimer,
    CLAIMS_URLS_SPANISH.msaDisclaimer
  )

  return (
    <section
      className={classNames(
        'ds-l-container',
        'ds-u-visibility--hidden-print',
        className
      )}
    >
      <div className="ds-l-row">
        <div className="ds-l-col--12">
          <h2 className="ds-text-heading--2xl">
            {t('accidentAndInjuryClaimsHeading')}
          </h2>
        </div>
      </div>
      <div className="ds-l-row">
        <div className="ds-u-margin-top--3 ds-u-margin-bottom--6 ds-l-md-col--6">
          <ContentCard
            heading={t('mspCasesHeading')}
            description={t('mspCasesDescription')}
            buttonLink={
              flags?.useNewMspLinks ? '/claims/msp' : mspButtonLegacyLink
            }
            buttonText={t('mspCasesSeeInfo')}
          />
        </div>
        <div className="ds-u-margin-top--3 ds-u-margin-bottom--6 ds-l-md-col--6">
          <ContentCard
            heading={t('msaCasesHeading')}
            description={t('msaCasesDescription')}
            buttonLink={
              flags?.useNewMsaLinks ? '/claims/msa' : msaButtonLegacyLink
            }
            buttonText={t('msaCasesSeeInfo')}
          />
        </div>
      </div>
    </section>
  )
}
